// Нижняя панель: поле ввода + крупные кнопки «отправить» и «микрофон».
// Пока агент думает/отвечает — «отправить» превращается в «Стоп» (квадрат).
// Микрофон справа: нейтральный в покое, акцентный с пульсацией при записи,
// спиннер при распознавании речи.

import { useState, type FormEvent } from 'react'
import { agentIconUrl } from '../net/mode'
import type { AgentIdentity } from '../types'

interface Props {
  onSend: (text: string) => void
  onStop: () => void
  onMic: () => void
  busy: boolean
  recording: boolean
  transcribing: boolean
  micLevel?: number
  agent?: AgentIdentity | null
  disabled?: boolean
}

export function TextInputBar({
  onSend,
  onStop,
  onMic,
  busy,
  recording,
  transcribing,
  micLevel = 0,
  agent,
  disabled,
}: Props) {
  const [value, setValue] = useState('')

  const submit = (e: FormEvent) => {
    e.preventDefault()
    const text = value.trim()
    if (!text) return
    onSend(text)
    setValue('')
  }

  const icon = agentIconUrl(agent?.id)
  // Лёгкое кольцо-свечение микрофона по уровню звука при записи.
  const micGlow = recording ? { boxShadow: `0 0 0 ${Math.round(4 + micLevel * 12)}px rgba(91,140,255,.16)` } : undefined

  return (
    <form className="textbar" onSubmit={submit}>
      {icon ? (
        <img className="textbar__avatar" src={icon} alt="" title={agent?.name} />
      ) : (
        agent?.emoji && <span className="textbar__avatar textbar__avatar--emoji" title={agent?.name}>{agent.emoji}</span>
      )}
      <input
        className="textbar__input"
        type="text"
        inputMode="text"
        placeholder="Написать агенту…"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        disabled={disabled}
        enterKeyHint="send"
        autoComplete="off"
        autoCorrect="on"
        aria-label="Текстовое сообщение агенту"
      />
      {busy ? (
        <button
          className="textbar__btn textbar__send textbar__stop"
          type="button"
          onClick={onStop}
          aria-label="Остановить ответ агента"
          title="Остановить"
        >
          <span className="stop-square" />
        </button>
      ) : (
        <button
          className="textbar__btn textbar__send"
          type="submit"
          disabled={disabled || !value.trim()}
          aria-label="Отправить"
        >
          <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
            <path fill="currentColor" d="M3.4 20.4 21 12 3.4 3.6 3 10l12 2-12 2 .4 6.4Z" />
          </svg>
        </button>
      )}
      <button
        type="button"
        className={`textbar__btn textbar__mic ${recording ? 'textbar__mic--on' : ''}`}
        onClick={onMic}
        disabled={transcribing}
        style={micGlow}
        aria-pressed={recording}
        aria-label={recording ? 'Остановить запись' : 'Говорить'}
        title={recording ? 'Остановить запись' : 'Говорить'}
      >
        {transcribing ? <span className="spinner spinner--btn" /> : <MicGlyph />}
      </button>
    </form>
  )
}

function MicGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">
      <path fill="currentColor" d="M12 14a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v5a3 3 0 0 0 3 3Z" />
      <path
        fill="currentColor"
        d="M17 11a1 1 0 1 0-2 0 3 3 0 0 1-6 0 1 1 0 1 0-2 0 5 5 0 0 0 4 4.9V18H8a1 1 0 1 0 0 2h8a1 1 0 1 0 0-2h-3v-2.1A5 5 0 0 0 17 11Z"
      />
    </svg>
  )
}
